"use client";

import { useIsDark } from "@/context/ThemeContext";
import { twMerge } from "tailwind-merge";
import Image from "next/image";
import Link from "next/link";
import { PortableText } from "@portabletext/react";
import { PortableTextComponents } from "@/components/blog/PortableTextComponents";
import { urlFor } from "@/sanity/lib/image";
import BlogSocialShare from "@/components/blog/BlogSocialShare";

const defaultImage = "/assets/blogPage/blogImage.png";

export default function BlogOverViewPageClient({ post, url }) {
  const isdark = useIsDark();

  if (!post) {
    return (
      <div className="text-center py-20 text-gray-400">Post not found</div>
    );
  }

  return (
    <div
      className={twMerge(
        "relative flex flex-col items-center justify-center w-full 4xl:max-w-[2000px] 3xl:max-w-[1600px] 2xl:max-w-[1400px] xl:max-w-[1240px] lg:max-w-[1024px] md:max-w-[768px] sm:max-w-[640px] mx-auto p-4 sm:p-6 md:p-8 lg:p-10 rounded-[20px] lg:rounded-[40px] mt-16 2xl:mt-20 mb-10",
        isdark ? "bg-[#0F0F0F]" : "bg-[#F2E4FE]"
      )}
    >
      <div className="">
        <Image
          src={
            post.mainImage
              ? urlFor(post.mainImage).width(1600).height(800).url()
              : defaultImage
          }
          alt={post.title}
          width={1600}
          height={800}
          quality={95}
          priority
          fetchPriority="high"
          placeholder={post.mainImage?.asset?.metadata?.lqip ? "blur" : "empty"}
          blurDataURL={post.mainImage?.asset?.metadata?.lqip}
          className="object-cover rounded-lg
            w-[300px] h-[135px]
            mobile:w-[380px] mobile:h-[171px]
            mobile2:w-[460px] mobile2:h-[207px]
            sm:w-[540px] sm:h-[243px]
            md:w-[640px] md:h-[288px]
            tablet:w-[760px] tablet:h-[342px]
            lg:w-[900px] lg:h-[405px]
            laptop:w-[1160px] laptop:h-[522px]
            2xl:max-w-[1280px] 2xl:max-h-[576px]
            3xl:max-w-[1420px] 3xl:max-h-[639px]
            4xl:max-w-[1560px] 4xl:max-h-[702px]"
        />
      </div>

      <div
        className="flex flex-col sm:flex-row
        sm:w-full
        justify-between
        gap-[16px]
        sm:gap-[28px]
        md:gap-[40px]
        laptop:gap-[60px]
        2xl:gap-[70px]
        3xl:gap-[80px]
        4xl:gap-[90px]
        my-6 sm:my-10"
      >
        <GradientContent
          icon="/assets/blogPage/writer.svg"
          topic="Written By : "
          content={post.authorName || "Unknown"}
          authorSlug={post.authorSlug}
          isAuthor={true}
        />
        <GradientContent
          icon="/assets/blogPage/date.svg"
          topic="Posted On : "
          content={new Date(post.publishedAt).toDateString()}
        />
        <GradientContent
          icon="/assets/blogPage/type.svg"
          content={(post.categories ?? []).map((c) => c.title).join(", ")}
        />
      </div>

      <div className="w-full text-left mt-2 mb-3 sm:mt-4 sm:mb-8">
        <h1
          className={twMerge(
            "font-alexandria font-bold text-[20px] sm:text-[22px] md:text-[26px] lg:text-[30px] xl:text-[34px] leading-tight",
            isdark ? "text-white" : "text-[#0B1020]"
          )}
        >
          {post.title}
        </h1>
      </div>

      <div className="prose prose-invert max-w-none">
        <PortableText value={post.body} components={PortableTextComponents} />
      </div>

      <div className="flex flex-col justify-start w-full font-manrope font-medium">
        {post.authorSlug ? (
          <Link href={`/blog/author/${post.authorSlug}`}>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-[25px] text-foreground hover:underline transition-all duration-200 cursor-pointer">
              {post.authorName || "Priya Fernando"}
            </p>
          </Link>
        ) : (
          <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-[25px] text-foreground">
            {post.authorName || "Priya Fernando"}
          </p>
        )}
        <p
          className={twMerge(
            "font-alexandria font-normal text-[14px] sm:text-[15px] md:text-[16px] lg:text-[18px] laptop:text-[20px] 2xl:text-[22px] 3xl:text-[25px] 4xl:text-[28px]",
            isdark ? "text-[#7F7F7F]" : "text-[#5A5A6E]"
          )}
        >
          Writer
        </p>
      </div>

      <BlogSocialShare post={post} url={url} />
    </div>
  );
}

function GradientContent({ icon, topic, content, authorSlug, isAuthor }) {
  const isdark = useIsDark();
  return (
    <>
      <div className="flex items-center gap-[10px]">
        <img
          src={icon}
          alt="icon"
          width={28} // or your Tailwind responsive sizes
          height={28}
          sizes="28px"
          quality={70}
          className="w-[18px] h-[18px]
          sm:w-[21px] sm:h-[21px]
          md:w-[23px] md:h-[23px]
          lg:w-[25px] lg:h-[25px]
          laptop:w-[27px] laptop:h-[27px]
          2xl:w-[30px] 2xl:h-[30px]
          3xl:w-[34px] 3xl:h-[34px]
          4xl:w-[40px] 4xl:h-[40px]"
        />
        <p
          className={twMerge(
            "font-alexandria font-normal text-sm sm:text-base md:text-lg lg:text-xl xl:text-[22px] 4xl:text-2xl text-transparent bg-clip-text bg-gradient-to-r from-[#9C00BB]",
            isdark ? "to-[#FFFFFF]" : "to-[#1A1A1A]"
          )}
        >
          {topic}{" "}
          {isAuthor && authorSlug ? (
            <Link
              href={`/blog/author/${authorSlug}`}
              className="hover:underline transition-all duration-200 cursor-pointer"
            >
              {content}
            </Link>
          ) : (
            content
          )}
        </p>
      </div>
    </>
  );
}
